// QUERIES

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosError, AxiosResponse } from 'axios'
import { GenerateMarketingMaterialsPayloadDTO, MarketingMaterialDTO, MarketingPropertyDetailsDTO, SavePropertyDetailsPayloadDTO } from 'models/visualsMarketing'
import { EntityKeys, QueryType, getMutation } from 'utils/reactQuery'
import { JobType, useJobPoll } from './job.query'

import { VisualsMarketingTargetPlatform } from 'components/pages/VisualsMarketing/_main/VisulalsMarketing.constants'
import { JobDTO } from 'models/misc'
import { StatusResponse } from 'models/redux'
import { useAPI } from 'utils/API'

enum Endpoints {
  GET_PROPERTY_DETAILS = '/workspace/{workspaceId}/marketing/realty/{realtyId}/propertyDetails',
  GET_SAVED_MATERIALS = '/workspace/{workspaceId}/marketing/realty/{realtyId}/materials',
  SAVE_PROPERTY_DETAILS = '/workspace/{workspaceId}/marketing/realty/{realtyId}/propertyDetails',
  SAVE_MATERIAL = '/workspace/{workspaceId}/marketing/realty/{realtyId}/materials/{marketingMaterialId}',
  GENERATE_MATERIAL = '/workspace/{workspaceId}/marketing/realty/{realtyId}/materials/generate/text',
}

/** Query for getting saved property details */
export function useSavedMarketingPropertyDetails(realtyId: string, workspaceId: string) {
  const api = useAPI<Endpoints>()

  return useQuery({
    queryKey: [EntityKeys.VISUALS_MARKETING_PROPERTY_DETAILS, QueryType.GET, { realtyId }],
    queryFn: () => api.get<MarketingPropertyDetailsDTO>(Endpoints.GET_PROPERTY_DETAILS, { realtyId, workspaceId }, false),
    retry: false,
  })
}

/** Query for getting saved marketing materials (returns 404 if no materials are yet saved) */
export function useSavedMarketingMaterials(realtyId: string, workspaceId: string) {
  const api = useAPI<Endpoints>()

  return useQuery<Partial<Record<VisualsMarketingTargetPlatform, MarketingMaterialDTO>>, AxiosError>({
    queryKey: [EntityKeys.VISUALS_MARKETING_MATERIALS, QueryType.GET, { realtyId }],
    queryFn: () => api.get<Partial<Record<VisualsMarketingTargetPlatform, MarketingMaterialDTO>>>(
      Endpoints.GET_SAVED_MATERIALS,
      { realtyId, workspaceId },
      false
    ),
    enabled: !!realtyId,
    retry: false, // When there is no saved materials, API return 404'
  })
}

// MUTATIONS

/** Mutation for saving property details */
export function useSaveMarketingPropertyDetails() {
  const api = useAPI<Endpoints>()

  return getMutation<AxiosResponse<StatusResponse>, SavePropertyDetailsPayloadDTO & { onSuccess?: () => void }>(
    ({ realtyId, workspaceId, data }) => api.put(
      Endpoints.SAVE_PROPERTY_DETAILS,
      { realtyId, workspaceId },
      data,
      true
    ),
    (client, { realtyId, onSuccess }) => {
      client.invalidateQueries({ queryKey: [EntityKeys.VISUALS_MARKETING_PROPERTY_DETAILS, QueryType.GET, { realtyId }] })
      onSuccess?.()
    }
  )
}

/**
 * Mutation with flow for generating text
 * Call endpoint for receiving a jobID for generation and start longPolling it.
 * Resolve if getting jobId and long-polling succeeds
 * Reject otherwise
 */
export function useGenerateMarketingMaterial(targetPlatform: VisualsMarketingTargetPlatform) {
  const api = useAPI<Endpoints>()
  const jobPoll = useJobPoll(JobType.MARKETING_MATERIAL)

  return useMutation<boolean, unknown, GenerateMarketingMaterialsPayloadDTO>({
    mutationFn: async ({ realtyId, workspaceId, payload }) => {
      const job = await api.post<JobDTO>(
        Endpoints.GENERATE_MATERIAL,
        { realtyId, workspaceId: workspaceId ?? '' },
        {
          ...payload,
          targetPlatform
        },
        true
      )

      await jobPoll.mutateAsync({ jobId: job.data.id })
      return true
    }
  })
}

/** 
 * Mutation with combined saving and generating flow.
 * Saving can be skipped via flag - by default, saves always
*/
export function useSaveAndGenerateMarketingMaterial(targetPlatform: VisualsMarketingTargetPlatform) {
  const client = useQueryClient()

  const saveInfo = useSaveMarketingPropertyDetails()
  const generate = useGenerateMarketingMaterial(targetPlatform)

  return useMutation<boolean, unknown, GenerateMarketingMaterialsPayloadDTO & SavePropertyDetailsPayloadDTO & { shouldSave: boolean }>({
    mutationFn: async ({ realtyId, workspaceId, payload, data, shouldSave = true }) => {

      if (shouldSave) {
        try {
          await saveInfo.mutateAsync({ realtyId, workspaceId, data })
        } catch (e) {
          return Promise.reject(e)
        }
      }

      try {
        await generate.mutateAsync({ realtyId, workspaceId, payload })
      } catch (e) {
        return Promise.reject(e)
      }

      return Promise.resolve(true)
    },
    onSuccess: (_, { realtyId }) => client.invalidateQueries({ queryKey: [EntityKeys.VISUALS_MARKETING_MATERIALS, QueryType.GET, { realtyId }] })
  })
}

/** Mutation for updating text of a target platform */
export function useSaveMarketingMaterial() {
  const api = useAPI<Endpoints>()

  return getMutation<StatusResponse, { realtyId: string, workspaceId: string, id: string, text: string, platform: VisualsMarketingTargetPlatform, onSuccess?: () => void }>(
    ({ id, realtyId, workspaceId, text }) => api.put(
      Endpoints.SAVE_MATERIAL,
      {
        realtyId,
        workspaceId,
        marketingMaterialId: id
      },
      { value: text },
      false
    ),
    (client, { realtyId, platform, text, onSuccess }) => {
      client.setQueryData<Partial<Record<VisualsMarketingTargetPlatform, MarketingMaterialDTO>>>([EntityKeys.VISUALS_MARKETING_MATERIALS, QueryType.GET, { realtyId }], (existingData) => ({
        ...existingData,
        [platform]: {
          ...(existingData?.[platform] || {}),
          text
        }
      }))
      client.invalidateQueries({ queryKey: [EntityKeys.VISUALS_MARKETING_MATERIALS, QueryType.GET, { realtyId }] })
      onSuccess?.()
    }
  )
}
